/**
 * @file cusa-content.js
 *
 * This file is for both store content and ctg content.
 */

/**
 * @typedef {Record<string, any>} ContentData
 *
 * A content data object containing all necessary data to create new content. This is derived from JSON
 * created in `create-content-json.liquid`.
 */

/**
 * @typedef {Record<string, string>} ElementMappings
 *
 * Maps DOM element IDs to keys in a `ContentData` object.
 */

/**
 * @class CusaContent
 * @extends HTMLElement
 *
 * Custom element that renders and optionally cycles through content records embedded as JSON.
 * Also tracks user clicks for analytics purposes and syncs click data to cart attributes.
 */
class CusaContent extends HTMLElement {
	/**
	 * @constructor
	 *
	 * Initializes element state, reads dataset values, and prepares mappings for content rendering.
	 */
	constructor() {
		super();
		/**
		 * @type {boolean}
		 *
		 * Enables verbose logging for this element instance.
		 */
		this.SHOW_LOGS = true;
		/** @type {string | undefined} */
		this.contentId = this.dataset.contentId;
		/** @type {string | undefined} */
		this.contentSlot = this.dataset.contentSlot;
		/** @type {HTMLElement | null} */
		this.scriptEl = this.contentId && this.querySelector(`#${this.contentSlot}_contentData`);
		/** @type {ContentData[] | null} */
		this.jsonData = null;
		if (this.scriptEl) {
			// A malformed payload must not throw here: an exception in the constructor fails the
			// custom element upgrade, which silently disables click tracking for the whole slot.
			try {
				this.jsonData = JSON.parse(this.scriptEl.innerHTML);
			} catch (error) {
				console.error(`[CUSA Content] Invalid content JSON for slot ${this.contentSlot}. Cycling disabled for this slot.`, error);
			}
		}
		/** @type {HTMLTemplateElement | null} */
		this.templateEl = this.querySelector(`template`);
		/** @type {number} */
		this.currentIndex = 0;
		/**
		 * @type {number}
		 *
		 * Default to 6s in ms.
		 */
		this.cycleDuration = this.dataset.cycleDuration == 0 ? 6000 : this.dataset.cycleDuration;
		/** @type {HTMLElement | null} */
		this.listrakContentEl = this.querySelector(`#${this.contentSlot}_listrakContent`);
		/**
		 * @type {ElementMappings}
		 *
		 * Maps element IDs to their content slots.
		 */
		this.elementMappings = {
			[`${this.contentId}-page-content`]: 'page',
			[`${this.contentId}-link`]: 'getUrlfunction',
			[`${this.contentId}-media`]: 'media',
			[`${this.contentId}-title`]: 'title',
			[`${this.contentId}-text`]: 'text',
			[`${this.contentId}-button`]: 'button_text',
			[`${this.contentId}-snipe`]: 'snipe'
		};
	}

	/**
	 * Runs when the element is connected to the DOM.
	 * - Optionally waits for Listrak content, then merges it into `jsonData`.
	 * - Collects clickable elements and attaches click listeners.
	 * - Starts the content cycling interval (when applicable) and emits the first promotion view.
	 * @returns {void}
	 */
	connectedCallback() {
		if (this.listrakContentEl) {
			this.waitForListrakContent();
		}

		this.getClickableEls();

		if (this.cycleDuration && this.jsonData && this.jsonData.length > 1) {
			this.timer = setInterval(this.cycleContent, this.cycleDuration);
			// console.log(`Cycle duration: ${this.cycleDuration}s`);

			// on first render
			this.emit('view_promotion', this.jsonData[this.currentIndex]);
		}
		for (const el of this.trackedEls) {
			// console.log(el);
			el.addEventListener('click', this.handleTrackedClick, false);
		}
	}

	/**
	 * Runs when the element is disconnected from the DOM.
	 * @returns {void}
	 */
	disconnectedCallback() {
		// this.jsonData && console.log(this.jsonData);
	}

	/**
	 * This logs the current element.
	 * @returns {void}
	 */
	logThisElement = () => {
		console.log(this);
	};

	/**
	 * We can't reliably reach into a custom element from outside it to extract data, so we emit a custom event with the data we'll need, which bubbles through the DOM tree where listeners can pick it up.
	 * @param {string} eventName
	 * @param {Record<string, any>} detail
	 * @returns {boolean}
	 */
	emit(eventName = 'view_promotion', detail) {
		let event = new CustomEvent(eventName, {
			bubbles: true,
			cancelable: true,
			detail: detail
		});
		return this.dispatchEvent(event);
	}

	/**
	 * @async
	 * Waits for Listrak content to populate, then updates `jsonData` with that content and removes
	 * the Listrak DOM container after a short delay.
	 * @returns {Promise<void>}
	 */
	waitForListrakContent = async () => {
		console.time('Listrak content loaded');
		await waitForMutation({
			target: this.listrakContentEl,
			mutations: ['childList', 'subtree', 'characterData'],
			timeout: this.cycleDuration // in ms
		}).promise;

		console.timeLog('Listrak content loaded');
		this.updateJsonDataWithListrakContent();
		console.log('Listrak content loaded and JSON data updated!');
		setTimeout(() => {
			this.listrakContentEl.remove(); // clean up the DOM
		}, 1500);
	};

	/**
	 * Updates the jsonData array with the content from the listrak content divs
	 * created in the create-content-json.liquid snippet.
	 * @returns {void}
	 */
	updateJsonDataWithListrakContent = () => {
		const listrakHtml = this.listrakContentEl.innerHTML;
		const parser = new DOMParser();
		const listrakDoc = parser.parseFromString(listrakHtml, 'text/html');
		const listrakContentItems = listrakDoc.querySelectorAll('[data-content-handle]');
		// console.log(listrakContentItems);

		listrakContentItems.forEach((item) => {
			const handle = item.dataset.contentHandle;
			const matchingContentObj = this.jsonData.find((obj) => obj.handle === handle);
			if (matchingContentObj) {
				matchingContentObj.page = item.innerHTML;
				console.log(`Updated content object with handle ${handle} with listrak content.`);
				// console.log(matchingContentObj.page);
			}
		});
		// console.log(this.jsonData);
	};

	/**
	 * Adds all anchor tags, button elements, and elements with the button
	 * class to an array.
	 * @returns {void}
	 */
	getClickableEls = () => {
		this.trackedEls = [
			...this.querySelectorAll('a'),
			...this.querySelectorAll('button:not(a[id*="-link"] button)'),
			...this.querySelectorAll('.button:not(a[id*="-link"] .button)'),
			...this.querySelectorAll('[data-shg-href]:not(a[id*="-link"] [data-shg-href])')
		];
	};

	/**
	 * Adds [a] class[es] to an array of elements.
	 * @param {string} classString
	 * @param {Element[]} arrayOfEls
	 * @returns {void}
	 */
	attachClasses = (classString, arrayOfEls) => {
		for (const el of arrayOfEls) {
			el.classList.add(classString);
		}
	};

	/**
	 * Attaches a click event listener to all elements in the this.trackedEls array
	 * which executes the `handleTrackedClick` function on click.
	 * @returns {void}
	 */
	attachClickListeners = () => {
		this.getClickableEls();
		for (const el of this.trackedEls) {
			el.addEventListener('click', this.handleTrackedClick, false);
		}
	};

	/**
	 * Truncates (and normalizes) the cusa-content-clicks string to a maximum length.
	 * @param {string | null | undefined} contentStringArray
	 * @param {number} maxLength
	 * @returns {ReturnType<typeof normalizeCusaContentClicks>}
	 */
	truncateCusaContentClicks = (contentStringArray, maxLength) => {
		const splitStringArray = contentStringArray
			? contentStringArray.split(CUSA_CONTENT_CLICK_DELIMITER).filter(Boolean)
			: [];

		this.SHOW_LOGS && console.log('Truncating cusa-content-clicks if needed:');
		this.SHOW_LOGS && console.log(splitStringArray);

		const normalized = normalizeCusaContentClicks(contentStringArray, maxLength);
		if (normalized.didTruncate) {
			this.SHOW_LOGS && console.log(`${splitStringArray.length} exceeds max length of ${maxLength}. Truncating…`);
			this.SHOW_LOGS && console.log(`Truncated to ${normalized.length} items.`);
		} else {
			this.SHOW_LOGS && console.log(`No truncation needed. Length is ${normalized.length}.`);
		}
		return normalized;
	};

	/**
	 * @async
	 * On a click event, this sets or updates the local storage key for tracking
	 * CUSA content clicks. This also attempts to update the cart attributes.
	 * @returns {Promise<void>}
	 */
	handleTrackedClick = async () => {
		const clickedElContentId = this.dataset.cusaContentTrackingId;
		const storedCusaContentClicks = getLocalStorage('cusa-content-clicks');
		const updatedCusaContentClickString = storedCusaContentClicks
			? `${storedCusaContentClicks}||||${clickedElContentId}`
			: clickedElContentId;

		this.SHOW_LOGS &&
			console.log('[CUSA] Content click tracked:', {
				clickedElContentId,
				before: storedCusaContentClicks,
				after: updatedCusaContentClickString
			});

		const truncationData = this.truncateCusaContentClicks(updatedCusaContentClickString, CUSA_CONTENT_CLICK_MAX);

		setLocalStorage('cusa-content-clicks', truncationData.contentString);

		this.updateCusaContentClicksCartAttribute(truncationData.contentString);
		window.logCusaContentClickState();
	};

	/**
	 * A function to update the _cusa_content_clicks cart attribute or create it if it doesn't exist.
	 *
	 * @param {string} updatedCusaContentClickString The updated string of all CUSA content clicks stored in local storage.
	 * @returns {void}
	 */
	updateCusaContentClicksCartAttribute = (updatedCusaContentClickString) => {
		window.cusaCartAttributes = {
			...(window.cusaCartAttributes || {}),
			_cusa_content_clicks: updatedCusaContentClickString
		};
		if (typeof registerCartAttributeUpdate === 'function') {
			registerCartAttributeUpdate(
				{
					_cusa_content_clicks: updatedCusaContentClickString
				},
				this.SHOW_LOGS
			);
		}
		// Coded offer ads run their own add-to-cart scripts, which the quick-add hook never
		// sees. Every such add starts with a tracked click landing here, so announcing cart
		// activity from the click write opens the verify window for whatever add follows.
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			publish(PUB_SUB_EVENTS.cusaCartActivity, { source: 'cusa-content-click' });
		}
	};

	/**
	 * Moves the current index to the next content object.
	 * @returns {void}
	 */
	updateDataIndex = () => {
		// Increment the index and wrap around if necessary
		this.currentIndex = (this.currentIndex + 1) % this.jsonData.length;
		// If there's only one item, stop the timer
		if (this.jsonData.length === 1) {
			clearInterval(this.timer);
		}
	};

	/**
	 * A function which takes a content object and returns a url for <a> tag assignment based on logic to determine which url to use.
	 *
	 * @param {Record<string, any>} contentData An object containing all necessary data to create new content. This is derived from json created in `create-content-json.liquid`.
	 * @returns {string} A url to assign to an <a> tag.
	 */
	getLinkUrl = (contentData) => {
		let url = null;
		if (contentData.variant_url) {
			url = contentData.variant_url;
		} else if (contentData.product_url) {
			url = contentData.product_url;
		} else if (contentData.blog_article_url && contentData.blog_article_url !== '/blogs/news/') {
			url = contentData.blog_article_url;
		} else if (contentData.url) {
			url = contentData.url;
		} else {
			url = '/';
		}
		return url;
	};

	/**
	 * The script element within this custom element can't be accessed from outside it, hence this method
	 * @returns {ContentData | undefined}
	 */
	getJsonData = () => {
		return this.jsonData[this.currentIndex];
	};

	/**
	 * A function to recursively remove empty tags within the template clone. This is important to keep the DOM leaner and avoid keeping empty divs hanging around.
	 *
	 * @param {Node} parentEl The parent/current node in the cloned template's node tree.
	 * @returns {void}
	 */
	removeEmpties = (parentEl) => {
		const childrenArray = Array.from(parentEl.childNodes);
		childrenArray.forEach((child) => {
			if (child.nodeType === Node.ELEMENT_NODE) {
				// Page content is author-controlled HTML. Its empty elements are often placeholders
				// the page's own scripts fill in after render (e.g. the offer cards' headline and
				// price targets), so pruning inside it breaks those scripts. Leave it verbatim;
				// still drop the div itself when the entry has no page content at all.
				if (child.id && child.id.includes('-page-content')) {
					if (!child.hasChildNodes()) {
						parentEl.removeChild(child);
					}
					return;
				}
				// console.log(child);
				if (child.tagName === 'IMG' || child.tagName === 'VIDEO') {
					// Skip removal if it's an <img> or <video> with a non-empty src attribute, else, remove it.
					if (child.getAttribute('src') && child.getAttribute('src').trim() === '') {
						parentEl.removeChild(child);
					}
					return;
				}
				if (
					(child.textContent.trim() === '' && !child.hasChildNodes()) ||
					(child.id && child.id.includes('-snipe') && child.classList && child.classList.length === 1)
				) {
					parentEl.removeChild(child);
				}
				this.removeEmpties(child);
			}
		});
	};

	/**
	 * This function:
	 * 		1. Gets the content object at the current index of the contentData array
	 * 		2. Creates a clone of the content template
	 * 		3. Uses the content object data to fill out the clone appropriately
	 * 		4. Goes through the clone and removes any empty elements
	 *
	 * @returns {DocumentFragment | void} A cloned copy of the content template with only the necessary elements needed to create the desired content.
	 */
	createNewContent = () => {
		const contentData = this.jsonData[this.currentIndex]; // Use the current index
		if (!this.templateEl) return;

		const clone = document.importNode(this.templateEl.content, true);
		for (const [elId, contentSlot] of Object.entries(this.elementMappings)) {
			const el = clone.querySelector(`#${elId}`);

			// Handle pages
			if (el && contentSlot === 'page' && contentData[contentSlot] && contentData[contentSlot].trim() !== '') {
				el.innerHTML = unEscape(contentData[contentSlot]);
				clone.querySelector("[id*='-link']") && clone.removeChild(clone.querySelector("[id*='-link']"));
				// Handle links
			} else if (el && contentSlot === 'getUrlfunction') {
				el.href = this.getLinkUrl(contentData);
				// Handle snipes
			} else if (
				el &&
				contentSlot === 'snipe' &&
				contentData[contentSlot] &&
				contentData[contentSlot].trim() !== '' &&
				contentData[contentSlot].toLowerCase().trim() !== 'none'
			) {
				el.classList.add(contentData[contentSlot].toLowerCase());
				// Handle media & account for snipes if they exist
			} else if (el && contentSlot === 'media' && contentData[contentSlot] && contentData[contentSlot] !== '') {
				const snipeEl = el.querySelector(`#${this.contentId}-snipe`);
				let newContent = unEscape(contentData[contentSlot]);
				el.innerHTML = newContent;
				snipeEl && el.appendChild(snipeEl);
				// Handle everything else
			} else if (el && contentData[contentSlot] && contentData[contentSlot] !== '') {
				el.innerHTML = unEscape(contentData[contentSlot]);
			}
			// console.log(el);
		}

		this.removeEmpties(clone);
		this.setAttribute('data-cusa-content-tracking-id', `${this.contentSlot}ʅʕ^ᴥ^ʔʃ${contentData.name}`);
		// console.log(this.dataset.cusaContentTrackingId);

		return clone;
	};

	/**
	 * Scripts that arrive via innerHTML or template cloning are inert per the HTML spec, so
	 * page-content ads that depend on their own inline scripts (e.g. the TY modal offer cards)
	 * would go dead after the first cycle. Swapping each script element for a fresh copy makes
	 * the browser execute it; iteration is in document order and inline scripts run
	 * synchronously on insertion, so scripts still run in the order the page author wrote them.
	 * @param {HTMLElement} parentEl
	 * @returns {void}
	 */
	reviveScripts = (parentEl) => {
		for (const oldScript of parentEl.querySelectorAll('script')) {
			const newScript = document.createElement('script');
			for (const attr of oldScript.attributes) {
				newScript.setAttribute(attr.name, attr.value);
			}
			newScript.textContent = oldScript.textContent;
			oldScript.replaceWith(newScript);
		}
	};

	/**
	 * A function called by each interation of the `cycleContent` function which
	 * gets a document fragment from the `createNewContent` function.
	 * The parent element's inner html is cleared and then set to the document
	 * fragment.
	 * @returns {void}
	 */
	replaceContent = () => {
		const contentEl = document.querySelector(`#${this.contentId}`);
		contentEl.innerHTML = '';
		const newContent = this.createNewContent();

		newContent && contentEl.appendChild(newContent);
		newContent && this.reviveScripts(contentEl);
		// subsequent re-renders
		this.emit('view_promotion', this.jsonData[this.currentIndex]);

		this.attachClickListeners();
		this.attachClasses('content-img', [...this.querySelectorAll('img')]);
	};

	/**
	 * A function to move the first obj from the array of content data to the end
	 * and replace the contents of this parent element with the new content.
	 * @returns {void}
	 */
	cycleContent = () => {
		this.updateDataIndex();
		this.replaceContent();
	};
}

customElements.define('cusa-content', CusaContent);

// CUSA QUICK ADD
/**
 * @class CusaQuickAdd
 * @extends CusaContent
 *
 * Quick-add variant handler that posts to the cart, updates cart drawer/bubble sections, and manages
 * loading/disabled UI state for the quick-add button.
 */
class CusaQuickAdd extends CusaContent {
	/**
	 * @constructor
 Initializes quick-add state and sets up Escape key handling for the cart modal.
	 */
	constructor() {
		super();
		/** @type {string | undefined} */
		this.variantId = this.dataset.variantId;
		/** @type {HTMLButtonElement | null} */
		this.quickAddBtn = this.querySelector('.cusa-quick-add');
		/** @type {HTMLElement | null} */
		this.cart = document.querySelector('#CartDrawer');
		/** @type {any} */
		this.cartModal = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
		document.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.cartModal.close());
	}

	/**
	 * Attaches click handler for the quick-add button.
	 * @returns {void}
	 */
	connectedCallback() {
		this.quickAddBtn.addEventListener('click', (e) => {
			this.handleAddToCardClick(e);
		});
		// this.logThisElement();
	}

	/**
	 * Removes click handler for the quick-add button.
	 * @returns {void}
	 */
	disconnectedCallback() {
		this.quickAddBtn.removeEventListener('click', this.debouncedAddVariantToCart);
	}

	/**
	 * A function to clicks on this quick-add's ATC button
	 * @param {Event} e Event
	 * @returns {void}
	 */
	handleAddToCardClick = (e) => {
		this.quickAddBtn.querySelector('.metafield-rich_text_field').style.opacity = 0;
		this.quickAddBtn.querySelector('.loading__spinner').classList.remove('hidden');
		this.quickAddBtn.disabled = true;
		this.debouncedAddVariantToCart(e);
	};

	/**
	 * A function to reset this quick-add's ATC button back to the original appearance
	 * @returns {void}
	 */
	resetButton = () => {
		this.quickAddBtn.querySelector('.metafield-rich_text_field').style.opacity = 1;
		this.quickAddBtn.querySelector('.loading__spinner').classList.add('hidden');
		this.quickAddBtn.disabled = false;
	};

	/**
	 * @async
	 * A function to update the contents of the cart drawer after a quick-add event
	 *
	 * @param {string} json json data containing the current structure of the cart
	 * @returns {Promise<any>} a resolved promise
	 */
	updateCartContents = async (json) => {
		return new Promise((resolve) => {
			const cartParser = new DOMParser();
			const cartDrawerContents = cartParser
				.parseFromString(json.sections['cart-drawer'], 'text/html')
				.querySelector('#CartDrawer');
			this.cart.replaceWith(cartDrawerContents);
			this.cart = document.querySelector('#CartDrawer');
			this.cart.closest('cart-drawer').classList.remove('is-empty');
			this.cart.closest('cart-drawer').classList.add('drawer', 'animate');
			const activateTimeout = setTimeout(() => {
				this.cart.closest('cart-drawer').classList.add('active');
				this.resetButton();
				clearTimeout(activateTimeout);
			}, 250);
			resolve;
		});
	};

	/**
	 * A function to update the appearance of the cart count bubble after a quick-add
	 * ATC event
	 *
	 * @param {string} json json data representing the current state of the cart
	 * count bubble after an ATC event within this quick-add
	 * @returns {Promise<any>} a resolved promise
	 */
	updateCartCountBubble = (json) => {
		return new Promise((resolve) => {
			const cartBubbleParser = new DOMParser();
			const cartbBubbleContents = cartBubbleParser
				.parseFromString(json.sections['cart-icon-bubble'], 'text/html')
				.querySelector('.cart-count-bubble');
			// console.log('cartbBubbleContents = ');
			// console.log(cartbBubbleContents);
			const cartBubbleNumber = document.querySelector('.cart-count-bubble');
			cartBubbleNumber
				? cartBubbleNumber.replaceWith(cartbBubbleContents)
				: document.querySelector('#cart-icon-bubble').append(cartbBubbleContents);
			resolve;
		});
	};

	/**
	 * @async
	 * A function to post an update to the cart using the Shopify Cart API. This also
	 * gets the cart-drawer and cart-icon-bubble state after the update from the
	 * API call's returned data. Additionally, this waits for the cart-drawer to open
	 * and then focuses on the close button
	 * @returns {Promise<void>}
	 */
	addVariantToCart = async () => {
		let data = {};
		const qty = 1;
		data.sections = ['cart-drawer', 'cart-icon-bubble'];
		data.items = [
			{
				id: this.variantId,
				quantity: qty
			}
		];
		await fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json'
			},
			body: JSON.stringify(data)
		})
			.then((data) => data.json())
			.then((json) => {
				this.updateCartContents(json);
				return json;
			})
			.then((json) => {
				this.updateCartCountBubble(json);
				// Quick-add posts to /cart/add.js directly, so nothing publishes cartUpdate for it.
				// Announce the activity so the click-attribute verification pass runs after this add.
				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cusaCartActivity, { source: 'cusa-quick-add' });
				}
			})
			.then(() => {
				const overlay = document.querySelector('#CartDrawer-Overlay');
				overlay.addEventListener('click', () => this.cartModal.close());
				// console.log(document.activeElement);
				const delay = setTimeout(() => {
					this.cart.querySelector('.drawer__close').focus();
					// console.log(document.activeElement);
					clearTimeout(delay);
				}, 275);
			})
			.catch((e) => {
				console.error(e);
			})
			.finally(() => {});
	};

	/**
	 * A debounced version of the addVariantToCart method to prevent spamming
	 * the /cart/update.js endpoint with updates if users click cause options rapidly
	 * @returns {void}
	 */
	debouncedAddVariantToCart = debounce(() => {
		this.addVariantToCart();
	}, FETCH_DEBOUNCE_TIMER);
}
customElements.define('cusa-quick-add', CusaQuickAdd);
